<template>
  <div class="homepage">
    <!-- Hero区域 -->
    <section class="hero-section">
      <div class="section-container">
        <h1 class="hero-title">{{ $t('home.hero.title') }}</h1>
        <p class="hero-subtitle">
          {{ $t('home.hero.subtitle') }}
        </p>
        <div class="hero-features">
          <div class="feature-tag">
            <Icon name="material-symbols:speed" size="16" />
            {{ $t('home.features.fast') }}
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:format-paint" size="16" />
            {{ $t('home.features.format') }}
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:security" size="16" />
            {{ $t('home.features.secure') }}
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:translate" size="16" />
            {{ $t('home.features.languages') }}
          </div>
        </div>
      </div>
    </section>

    <!-- 翻译功能区域 -->
    <section class="translation-section">
      <div class="section-container">
        <CategoryTranslationForm
          category="general"
          @start-translation="handleStartTranslation"
        />
      </div>
    </section>


    <!-- SEO内容区域 -->
    <section class="seo-section">
      <div class="section-container">
        <SeoContent />
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
// @nuxt/icon 模块已自动提供 <Icon> 组件，无需手动导入
import CategoryTranslationForm from '~/components/shared/CategoryTranslationForm.vue'
import SeoContent from '~/components/shared/SeoContent.vue'
import { useI18n } from 'vue-i18n'

// 设置页面标题和meta信息
const { t } = useI18n()

useWebsiteSeo({
  title: () => t('seo.home.title'),
  description: () => t('seo.home.desc')
})

// 处理开始翻译事件（第一阶段为静态演示）
const handleStartTranslation = (settings: any) => {
  console.log('首页接收到翻译请求:', settings)

  // 第一阶段静态演示，实际功能将在第二阶段实现
  // TODO: 第二阶段将集成到"我的页面"中管理翻译任务
}

// 页面加载动画
onMounted(() => {
  // 添加页面加载完成的类
  document.body.classList.add('page-loaded')
})
</script>

<style scoped lang="scss">
@use 'sass:color';

.homepage {
  min-height: 100vh;
  background: linear-gradient(180deg,
    color.adjust($primary-color, $lightness: 35%) 0%,
    color.adjust($primary-color, $lightness: 42%) 15%,
    $gray-100 25%,
    color.adjust($gray-100, $lightness: -3%) 40%,
    color.adjust($accent-color, $lightness: 38%) 60%,
    color.adjust($accent-color, $lightness: 35%) 75%,
    color.adjust($primary-color, $lightness: 37%) 100%);
}

// Hero区域
.hero-section {
  background:
    linear-gradient(135deg,
      rgba(color.adjust($primary-color, $lightness: 28%), 0.85) 0%,
      rgba(color.adjust($accent-color, $lightness: 32%), 0.8) 25%,
      rgba(color.adjust($primary-color, $lightness: 35%), 0.75) 50%,
      rgba(color.adjust($accent-color, $lightness: 28%), 0.8) 75%,
      rgba(color.adjust($primary-color, $lightness: 32%), 0.85) 100%),
    url('/images/common/hero-main.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: $spacing-xxl 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba($primary-color, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba($accent-color, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba($primary-color, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg,
      transparent 0%,
      $primary-color 25%,
      $accent-color 50%,
      $primary-color 75%,
      transparent 100%);
    opacity: 0.6;
  }

  .section-container {
    position: relative;
    z-index: 1;
    text-align: center;
  }

  .hero-title {
    font-size: 3rem;
    font-weight: 800;
    color: $gray-900;
    margin: 0 0 $spacing-lg 0;
    line-height: 1.2;
    background: linear-gradient(135deg, $primary-color 0%, $accent-color 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: titleSlideIn 0.8s ease-out;
  }

  .hero-subtitle {
    font-size: 1.25rem;
    color: $gray-700;
    margin: 0 0 $spacing-xl 0;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    animation: subtitleSlideIn 0.8s ease-out 0.2s both;
  }

  .hero-features {
    display: flex;
    justify-content: center;
    gap: $spacing-md;
    flex-wrap: wrap;
    animation: featuresSlideIn 0.8s ease-out 0.4s both;

    .feature-tag {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: $spacing-xs;
      background: rgba($white, 0.9);
      padding: $spacing-xs $spacing-md;
      border-radius: $border-radius-lg;
      font-size: 0.9rem;
      font-weight: 500;
      color: $gray-700;
      box-shadow: $shadow-sm;
      transition: transform 0.2s ease;
      text-align: center;

      &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-md;
      }
    }
  }
}

// 各个区域的通用样式
section {
  padding: $spacing-xxl 0;

  .section-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 $spacing-lg;
  }
}

.translation-section {
  background: linear-gradient(135deg,
    color.adjust($primary-color, $lightness: 37%) 0%,
    $gray-100 25%,
    color.adjust($gray-100, $lightness: -2%) 50%,
    color.adjust($accent-color, $lightness: 38%) 75%,
    color.adjust($accent-color, $lightness: 35%) 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba($primary-color, 0.4) 50%,
      transparent 100%);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba($accent-color, 0.4) 50%,
      transparent 100%);
  }
}


.seo-section {
  background: linear-gradient(135deg,
    color.adjust($accent-color, $lightness: 35%) 0%,
    color.adjust($accent-color, $lightness: 38%) 20%,
    $gray-100 40%,
    color.adjust($gray-100, $lightness: -3%) 60%,
    color.adjust($primary-color, $lightness: 42%) 80%,
    color.adjust($primary-color, $lightness: 37%) 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 70% 30%, rgba($accent-color, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 30% 70%, rgba($primary-color, 0.12) 0%, transparent 40%),
      url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><defs><pattern id="dots" width="80" height="80" patternUnits="userSpaceOnUse"><circle cx="40" cy="40" r="1.2" fill="%23ffffff" fill-opacity="0.15"/></pattern></defs><rect width="100%" height="100%" fill="url(%23dots)"/></svg>');
    pointer-events: none;
  }
}


// 动画定义
@keyframes titleSlideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes subtitleSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes featuresSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 响应式设计
@media (max-width: 768px) {
  .hero-section {
    padding: $spacing-xl 0;
    background-position: center center;
    background-size: cover;

    .hero-title {
      font-size: 2rem;
    }

    .hero-subtitle {
      font-size: 1.1rem;
    }

    .hero-features {
      .feature-tag {
        font-size: 0.8rem;
        padding: $spacing-xs $spacing-sm;
      }
    }
  }

  section {
    padding: $spacing-lg 0;

    .section-container {
      padding: 0 $spacing-md;
    }
  }

}

@media (min-width: 769px) and (max-width: 1024px) {
  .hero-title {
    font-size: 2.5rem;
  }

}

// 页面加载动画
:global(body.page-loaded) {
  .hero-section {
    animation: pageSlideIn 0.6s ease-out;
  }

  section:not(.hero-section) {
    animation: sectionFadeIn 0.8s ease-out;
  }
}

@keyframes pageSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes sectionFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 滚动优化
html {
  scroll-behavior: smooth;
}

// 区域间的分隔线
.translation-section,
.seo-section {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 100px;
    height: 2px;
    background: linear-gradient(90deg,
      transparent 0%,
      $primary-color 50%,
      transparent 100%);
    transform: translateX(-50%);
  }
}

</style>
